import React from 'react';

import {
    InvestmentHoldingModel,
    InvestmentHoldingSubtype,
    InvestmentHoldingType
} from 'src/types/investment-holding.model';
import { DebtModel, DebtModelSubtype, DebtModelType } from 'src/types/debt.model';
import { ManualAccount, ManualAccountType } from 'src/types/manualAccount.model';

import DebtService from 'src/services/debt.service';
import InvestmentHoldingService from 'src/services/investment-holding.service';
import LiteralsService from 'src/services/literals/LiteralsService';

import CashForm from '../components/atomic/pages/addAccounts/forms/cashAndBanking/cashForm/CashForm';
import CheckingAccountForm from '../components/atomic/pages/addAccounts/forms/cashAndBanking/checkingAccountForm/CheckingAccountForm';
import SavingAccountForm from '../components/atomic/pages/addAccounts/forms/cashAndBanking/savingAccountForm/SavingAccountForm';
import HighYieldSavingAccountForm from '../components/atomic/pages/addAccounts/forms/cashAndBanking/highYieldSavingAccountForm/HighYieldSavingAccountForm';
import OtherCashAndBankingForm from '../components/atomic/pages/addAccounts/forms/cashAndBanking/otherCashAndBankingForm/OtherCashAndBankingForm';
import Form401kAnd403b from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/form401kAnd403b/Form401kAnd403b';
import EducationForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/educationForm/EducationForm';
import CertificateOfDepositeForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/certificateOfDepositForm/CertificateOfDepositForm';
import PrivateStockForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/privateStockForm/PrivateStockForm';
import MutualFundsAndPublicStocksForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/mutualFundsAndPublicStocksForm/MutualFundsAndPublicStocksForm';
import IraForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/iraForm/IraForm';
import PensionForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/pensionForm/PensionForm';
import BondsForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/bondsForm/BondsForm';
import CryptocurrencyForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/cryptocurrencyForm/CryptocurrencyForm';
import RealEstateForm from '../components/atomic/pages/addAccounts/forms/propertyAndAssets/realEstateForm/RealEstateForm';
import VehicleForm from '../components/atomic/pages/addAccounts/forms/propertyAndAssets/vehicleForm/VehicleForm';
import CustomPropertyAndAssetsForm from '../components/atomic/pages/addAccounts/forms/propertyAndAssets/customPropertyAndAssetsForm/CustomPropertyAndAssetsForm';
import MortgageForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/mortgageForm/MortgageForm';
import AutoLoanForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/autoLoanForm/AutoLoanForm';
import StudentLoanForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/studentLoanForm/StudentLoanForm';
import PersonalLoanForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/personalLoanForm/PersonalLoanForm';
import CreditCardDebtForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/creditCardDebtForm/CreditCardDebtForm';
import OtherDebtForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/otherDebtForm/OtherDebtForm';
import OtherInvestmentsAndRetirementForm from '../components/atomic/pages/addAccounts/forms/investmentsAndRetirement/otherInvestmentsAndRetirementForm/OtherInvestmentsAndRetirementForm';
import HomeEquityLoanForm from '../components/atomic/pages/addAccounts/forms/loansAndDebts/homeEquityLoanForm/HomeEquityLoanForm';

/** Label and form dependencies for all add account forms */
interface AddAccountFormValue {
    label: string;
    renderForm: (
        manualAccount: ManualAccount,
        onSubmit: (
            formValues: any,
            manualAccount: ManualAccount,
            isDebt: boolean,
            type?: string,
            subtype?: string
        ) => Promise<void>,
        handleBack: () => void
    ) => JSX.Element;
}

class ManualAccountService {
    public static cashAndBankingAccountTypes: ManualAccountType[] = [
        ManualAccountType.Cash,
        ManualAccountType.CheckingAccount,
        ManualAccountType.SavingAccount,
        ManualAccountType.HighYieldSavingAccount,
        ManualAccountType.OtherCashAndBanking
    ];

    public static investmentsAndRetirementAccountTypes: ManualAccountType[] = [
        ManualAccountType.Education,
        ManualAccountType.CertificateOfDeposit,
        ManualAccountType.PrivateStock,
        ManualAccountType.MutualFundsAndPublicStocks,
        ManualAccountType['401kAnd403b'],
        ManualAccountType.Ira,
        ManualAccountType.Pension,
        ManualAccountType.Bonds,
        ManualAccountType.Cryptocurrency,
        ManualAccountType.OtherInvestmentsAndRetirement
    ];

    public static propertyAndAssetsAccountTypes: ManualAccountType[] = [
        ManualAccountType.RealEstate,
        ManualAccountType.Vehicle,
        ManualAccountType.CustomPropertyAndAsset
    ];

    public static loansAndDebtsAccountTypes: ManualAccountType[] = [
        ManualAccountType.Mortgage,
        ManualAccountType.HomeEquityLoan,
        ManualAccountType.StudentLoan,
        ManualAccountType.AutoLoan,
        ManualAccountType.PersonalLoan,
        ManualAccountType.CreditCardDebt,
        ManualAccountType.OtherDebt
    ];

    public static yearsOptions = [10, 15, 20, 30];

    public static monthsOptions = [12, 24, 36, 48, 60, 72, 80];

    /** Record of each add account form and label */
    public static addAccountForms: Record<ManualAccountType, AddAccountFormValue> = {
        // cash and banking
        cash: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.cash'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CashForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        checkingAccount: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.checkingAccount'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CheckingAccountForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        savingAccount: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.savingAccount'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <SavingAccountForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        highYieldSavingAccount: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.highYieldSavingAccount'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <HighYieldSavingAccountForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        otherCashAndBanking: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.otherCashAndBanking'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <OtherCashAndBankingForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },

        // investments and retirement
        education: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.education'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <EducationForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        certificateOfDeposit: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.certificateOfDeposit'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CertificateOfDepositeForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        privateStock: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.privateStock'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <PrivateStockForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        mutualFundsAndPublicStocks: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.mutualFundsAndPublicStocks'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <MutualFundsAndPublicStocksForm
                    manualAccount={manualAccount}
                    onSubmit={onSubmit}
                    handleBack={handleBack}
                />
            )
        },
        '401kAnd403b': {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.401kAnd403b'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <Form401kAnd403b manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        ira: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.ira'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <IraForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        pension: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.pension'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <PensionForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        bonds: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.bonds'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <BondsForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        cryptocurrency: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.cryptocurrency'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CryptocurrencyForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        otherInvestmentsAndRetirement: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.otherInvestmentsAndRetirement'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <OtherInvestmentsAndRetirementForm
                    manualAccount={manualAccount}
                    onSubmit={onSubmit}
                    handleBack={handleBack}
                />
            )
        },

        // property and assets
        realEstate: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.realEstate'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <RealEstateForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        vehicle: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.vehicle'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <VehicleForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        customPropertyAndAsset: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.customPropertyAndAsset'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CustomPropertyAndAssetsForm
                    manualAccount={manualAccount}
                    onSubmit={onSubmit}
                    handleBack={handleBack}
                />
            )
        },

        // loans and debts
        mortgage: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.mortgage'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <MortgageForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        homeEquityLoan: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.homeEquityLoan'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <HomeEquityLoanForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        studentLoan: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.studentLoan'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <StudentLoanForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        autoLoan: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.autoLoan'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <AutoLoanForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        personalLoan: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.personalLoan'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <PersonalLoanForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        creditCardDebt: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.creditCardDebt'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <CreditCardDebtForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        },
        otherDebt: {
            label: LiteralsService.getLiteral('Onboarding.addAccounts.otherDebt'),
            renderForm: (manualAccount, onSubmit, handleBack) => (
                <OtherDebtForm manualAccount={manualAccount} onSubmit={onSubmit} handleBack={handleBack} />
            )
        }
    };

    /** On submit for all forms, includes investment holdings and debts */
    public static async upsertManualAccount(
        formValues: any,
        manualAccount: ManualAccount,
        isDebt: boolean,
        type?: string,
        subtype?: string
    ): Promise<void> {
        switch (true) {
            case !!manualAccount?.investmentHolding:
                await InvestmentHoldingService.updateInvestmentHoldingFromFormik(
                    formValues,
                    manualAccount.investmentHolding
                );
                break;
            case !!manualAccount?.debt:
                await DebtService.updateDebtFromFormik(formValues, manualAccount.debt);
                break;
            case !isDebt:
                await InvestmentHoldingService.createInvestmentHoldingFromFormik(formValues, type, subtype);
                break;
            default:
                await DebtService.createDebtFromFormik(formValues, type, subtype);
                break;
        }
    }

    // cash and banking
    public static isCash = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Cash &&
        !manualAccount?.investmentHolding?.subtype;

    public static isCheckingAccount = (manualAccount: ManualAccount): boolean =>
        InvestmentHoldingType.Cash &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.CheckingAccount;

    public static isSavingAccount = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Cash &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.SavingAccount;

    public static isHighYieldSavingAccount = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Cash &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.HighYieldSavingAccount;

    public static isOtherCashAndBanking = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Cash &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.OtherCashAndBanking;

    public static isCashAndBanking = (manualAccount: ManualAccount): boolean =>
        this.isCash(manualAccount) ||
        this.isCheckingAccount(manualAccount) ||
        this.isSavingAccount(manualAccount) ||
        this.isHighYieldSavingAccount(manualAccount) ||
        this.isOtherCashAndBanking(manualAccount);

    // investments and retirement
    public static isEducation = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.College;

    public static isCd = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Cash &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.CertificateOfDeposit;

    public static isPrivateStock = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.PrivateStock;

    public static isPublicStock = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Stock;

    public static is401kAnd403b = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Retire &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype['401kAnd403b'];

    public static isIra = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Retire &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.Ira;

    public static isPension = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Retire &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.Pension;

    public static isBonds = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Bonds;

    public static isCrypto = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Crypto;

    public static isOtherInvestmentsAndRetirement = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Other &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.OtherInvestmentsAndRetirement;

    public static isInvestmentsAndRetirment = (manualAccount: ManualAccount): boolean =>
        this.isEducation(manualAccount) ||
        this.isCd(manualAccount) ||
        this.isPrivateStock(manualAccount) ||
        this.isPublicStock(manualAccount) ||
        this.is401kAnd403b(manualAccount) ||
        this.isIra(manualAccount) ||
        this.isPension(manualAccount) ||
        this.isBonds(manualAccount) ||
        this.isCrypto(manualAccount) ||
        this.isOtherInvestmentsAndRetirement(manualAccount);

    // property and assets
    public static isRealEstate = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Custom &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.RealEstate;

    public static isVehicle = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Custom &&
        manualAccount?.investmentHolding?.subtype === InvestmentHoldingSubtype.Vehicles;

    public static isCustomPropertyAndAsset = (manualAccount: ManualAccount): boolean =>
        manualAccount?.investmentHolding?.type === InvestmentHoldingType.Custom &&
        !manualAccount?.investmentHolding?.subtype;

    public static isPropertyAndAssets = (manualAccount: ManualAccount): boolean =>
        this.isRealEstate(manualAccount) ||
        this.isVehicle(manualAccount) ||
        this.isCustomPropertyAndAsset(manualAccount);

    // loans and debts
    public static isMortgage = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Mortgage;

    public static isHomeEquityLoan = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Other &&
        manualAccount?.debt?.subtype === DebtModelSubtype.HomeEquityLoan;

    public static isStudentLoan = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Student;

    public static isAutoLoan = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Auto;

    public static isPersonalLoan = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Personal;

    public static isCreditCardDebt = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.CreditCard;

    public static isOtherDebt = (manualAccount: ManualAccount): boolean =>
        manualAccount?.debt?.type === DebtModelType.Other;

    public static isLoansAndDebts = (manualAccount: ManualAccount): boolean =>
        this.isMortgage(manualAccount) ||
        this.isHomeEquityLoan(manualAccount) ||
        this.isStudentLoan(manualAccount) ||
        this.isAutoLoan(manualAccount) ||
        this.isPersonalLoan(manualAccount) ||
        this.isCreditCardDebt(manualAccount) ||
        this.isOtherDebt(manualAccount);

    /** Gets type from a manual account, used to lookup in addAccountForms */
    public static getManualAccountType(manualAccount: ManualAccount): ManualAccountType {
        switch (true) {
            // cash and banking
            case this.isCash(manualAccount):
                return ManualAccountType.Cash;
            case this.isCheckingAccount(manualAccount):
                return ManualAccountType.CheckingAccount;
            case this.isSavingAccount(manualAccount):
                return ManualAccountType.SavingAccount;
            case this.isHighYieldSavingAccount(manualAccount):
                return ManualAccountType.HighYieldSavingAccount;
            case this.isOtherCashAndBanking(manualAccount):
                return ManualAccountType.OtherCashAndBanking;

            // investments and retirement
            case this.isEducation(manualAccount):
                return ManualAccountType.Education;
            case this.isCd(manualAccount):
                return ManualAccountType.CertificateOfDeposit;
            case this.isPrivateStock(manualAccount):
                return ManualAccountType.PrivateStock;
            case this.isPublicStock(manualAccount):
                return ManualAccountType.MutualFundsAndPublicStocks;
            case this.is401kAnd403b(manualAccount):
                return ManualAccountType['401kAnd403b'];
            case this.isIra(manualAccount):
                return ManualAccountType.Ira;
            case this.isPension(manualAccount):
                return ManualAccountType.Pension;
            case this.isBonds(manualAccount):
                return ManualAccountType.Bonds;
            case this.isCrypto(manualAccount):
                return ManualAccountType.Cryptocurrency;
            case this.isOtherInvestmentsAndRetirement(manualAccount):
                return ManualAccountType.OtherInvestmentsAndRetirement;

            // property and assets
            case this.isRealEstate(manualAccount):
                return ManualAccountType.RealEstate;
            case this.isVehicle(manualAccount):
                return ManualAccountType.Vehicle;
            case this.isCustomPropertyAndAsset(manualAccount):
                return ManualAccountType.CustomPropertyAndAsset;

            // loans and debts
            case this.isMortgage(manualAccount):
                return ManualAccountType.Mortgage;
            case this.isHomeEquityLoan(manualAccount):
                return ManualAccountType.HomeEquityLoan;
            case this.isStudentLoan(manualAccount):
                return ManualAccountType.StudentLoan;
            case this.isAutoLoan(manualAccount):
                return ManualAccountType.AutoLoan;
            case this.isPersonalLoan(manualAccount):
                return ManualAccountType.PersonalLoan;
            case this.isCreditCardDebt(manualAccount):
                return ManualAccountType.CreditCardDebt;
            case this.isOtherDebt(manualAccount):
                return ManualAccountType.OtherDebt;
        }
    }

    /** Refresh all user accounts including visible-accounts, hidden-accounts, investment-holdings, and debts */
    public static async getAccounts(): Promise<{
        newInvestmentHoldings: InvestmentHoldingModel[];
        newDebts: DebtModel[];
    }> {
        const [newInvestmentHoldings, newDebts] = await Promise.all([
            InvestmentHoldingService.getInvestmentHoldings(),
            DebtService.getDebts()
        ]);

        return {
            newInvestmentHoldings,
            newDebts
        };
    }

    /** Delete a manual account including investment-holding or debt */
    public static async deleteAccount(manualAccount: ManualAccount): Promise<void> {
        if (manualAccount?.investmentHolding) {
            await InvestmentHoldingService.deleteInvestmentHolding(manualAccount.investmentHolding.id);
        } else if (manualAccount?.debt) {
            await DebtService.deleteDebt(manualAccount.debt);
        }
    }
}

export default ManualAccountService;
